import { fetchAuthSession } from "aws-amplify/auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PageLoadingSpinner from "../PageLoadingSpinner";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { logger } from "../../logger";
import { useFayAuth } from "../../auth/useFayAuth";
import { clearActiveUserId } from "../../../fay/axiosInstance";

export interface AWSCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
  expiration?: Date;
}

export const useAuthSession = (
  forceRefresh: boolean = true,
  reloadOnFocus: boolean = true,
) => {
  const { data, isPending } = useQuery({
    queryKey: ["authSession"],
    queryFn: async () => {
      return await fetchAuthSession({ forceRefresh });
    },
    refetchInterval: 1000 * 60 * 59, // 59 minutes (access token valid for 1 hour)
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: reloadOnFocus,
  });

  return {
    authSession: data,
    isPending,
  };
};

const useLoggedInAuth = () => {
  const { isLoggedIn, signOut: rootSignOut } = useFayAuth();

  const {
    data: authSession,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["loggedInAuthSession"],
    queryFn: async () => {
      logger.info("Refetching auth credentials");
      return await fetchAuthSession({ forceRefresh: true });
    },
    enabled: isLoggedIn,
    refetchInterval: 1000 * 60 * 59, // 59 minutes (access token valid for 1 hour)
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 59, // 59 minutes (access token valid for 1 hour)
  });

  // New auth requires us to refetch when isLoggedIn changes
  // Only want to change on isLoggedIn changing, other deps changing are not
  // relevant to this effect.
  useEffect(() => {
    (async () => {
      if (isLoggedIn) {
        await refetch();
      }
    })();
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const cognitoId = authSession?.userSub ?? null;
  const credentials = authSession?.credentials ?? null;

  const jwt = authSession?.tokens?.idToken?.toString() ?? null;

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const signOut = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["authSession"] });
    await queryClient.invalidateQueries({ queryKey: ["loggedInAuthSession"] });
    queryClient.clear();
    clearActiveUserId();
    navigate(process.env.REACT_APP_FAY_APP === "provider" ? "/provider" : "/");
    rootSignOut();
  }, [queryClient, navigate, rootSignOut]);

  return {
    cognitoId,
    credentials,
    jwt,
    isPending,
    signOut,
  };
};

export type AuthenticatedContextType = {
  cognitoId: string;
  credentials: AWSCredentials | null;
  jwt: string;
  signOut: () => void;
};

const AuthenticatedContext = createContext<AuthenticatedContextType | null>(
  null,
);

const useAuth = (): AuthenticatedContextType => {
  const authenticatedContext = useContext(AuthenticatedContext);

  if (!authenticatedContext) {
    throw new Error("Not authenticated");
  }

  return authenticatedContext;
};

export const LoadWhileWaitingForAuth = ({
  children,
  loadingMessage,
}: {
  children: React.ReactNode;
  loadingMessage?: string;
}) => {
  const { cognitoId, jwt, ...rest } = useLoggedInAuth();

  if (cognitoId === null || jwt === null) {
    return <PageLoadingSpinner loadingMessage={loadingMessage} />;
  }

  return (
    <AuthenticatedContext.Provider value={{ cognitoId, jwt, ...rest }}>
      {children}
    </AuthenticatedContext.Provider>
  );
};

export default useAuth;
